import React, { Component } from 'react'
import { Col, Row } from 'reactstrap'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import {
  Divider,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material'
import { withFirebase } from '../Fb'

export const PARTNER_STATES = [
  { label: 'Aktiivinen', value: 'active' },
  { label: 'Tauolla', value: 'paused' },
]

const INVOICING_MODES = [
  { label: 'Kumppani laskuttaa', value: 'partnerinvoices' },
  { label: 'Laskutamme kumppanin puolesta', value: 'selfbill' },
]

const VAT_OPTIONS = [0, 10, 13.5, 14, 24, 25.5]

export const EMPTY_PARTNER = {
  name: '',
  organisationid: '',
  organisationname: '',
  contactname: '',
  email: '',
  phone: '',
  businessid: '',
  vatpercent: 25.5,
  invoicingmode: 'partnerinvoices',
  iban: '',
  notes: '',
  state: 'active',
}

/**
 * Create/edit form for an affiliate partner - the billable party behind one or more
 * campaigns. The organisation link lives here rather than on the campaign because the
 * partner is who gets invoiced, and campaigns come and go beneath one partner.
 */
class AffiliatePartnerEditor extends Component {
  constructor(props) {
    super(props)
    // The spread is shallow, but a partner has no nested objects, so seeding is flat.
    this.state = {
      partner: { ...EMPTY_PARTNER, ...(props.partner || {}) },
      organisations: [],
      prhSearching: false,
      prhResults: null,
      saving: false,
    }
  }

  componentDidMount() {
    // The venue's own organisations, fetched once. Deliberately not the Algolia
    // PersonAndOrganisationSearch: that reads firebase.searchCombined, whose Fuse cache is
    // filled by other views, so it would come up empty when this page is opened directly.
    this.props.firebase
      .listenOrganisations()
      .get()
      .then((snapshot) => {
        const organisations = snapshot.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .filter((o) => o.state !== 'deleted')
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'fi'))
        this.setState({ organisations })
      })
      .catch((error) => console.error('Affiliate: organisations failed to load', error))
  }

  handleTextChange = (e, property) => {
    const { partner } = this.state
    partner[property] = e.target.value
    this.setState({ partner })
  }

  handleNumberChange = (e, property) => {
    const { partner } = this.state
    const raw = e.target.value
    if (typeof raw === 'number') {
      partner[property] = raw
    } else {
      partner[property] = parseFloat(String(raw).replace(',', '.').replace(/[^0-9.]/g, '')) || 0
    }
    this.setState({ partner })
  }

  handleStateSwitch = (e) => {
    const { partner } = this.state
    partner.state = e.target.checked ? 'active' : 'paused'
    this.setState({ partner })
  }

  // Both the id and the name are stored: the name so the campaign list renders without a
  // second fetch, the id so it can still be traced back.
  handleOrganisationChange = (organisation) => {
    const { partner } = this.state
    partner.organisationid = organisation ? organisation.id : ''
    partner.organisationname = organisation ? organisation.name || '' : ''
    if (organisation && organisation.businessId && !partner.businessid) {
      partner.businessid = organisation.businessId
    }
    if (organisation && !partner.name) {
      partner.name = organisation.name || ''
    }
    this.setState({ partner })
  }

  // For a partner that is not an organisation on file yet, look the company up in the
  // Finnish business registry so the name and business ID are typed once, correctly.
  searchPrh = async () => {
    const { partner } = this.state
    const term = partner.businessid || partner.name
    if (!term) return
    this.setState({ prhSearching: true, prhResults: null })
    const result = await this.props.firebase.findOrganisationFromPrh(partner.businessid, partner.name)
    const hits = result && result.data && !result.data.empty && Array.isArray(result.data) ? result.data : []
    this.setState({ prhSearching: false, prhResults: hits })
  }

  applyPrhHit = (hit) => {
    const { partner } = this.state
    partner.name = hit.name || partner.name
    partner.businessid = hit.businessId || partner.businessid
    this.setState({ partner, prhResults: null })
  }

  isValid = () => {
    const { partner } = this.state
    return !!(partner.name && partner.name.trim().length > 0)
  }

  handleSave = async () => {
    if (!this.isValid()) return
    this.setState({ saving: true })
    await this.props.onSave(this.state.partner)
    this.setState({ saving: false })
  }

  renderPrhResults = () => {
    const { prhResults, prhSearching } = this.state
    if (prhSearching) return <CircularProgress size={18} sx={{ mt: 1 }} />
    if (!prhResults) return null
    if (prhResults.length === 0) {
      return (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Ei tuloksia yritysrekisteristä.
        </Typography>
      )
    }
    return (
      <div style={{ marginTop: 8 }}>
        {prhResults.slice(0, 8).map((hit, index) => (
          <Row key={hit.businessId || index} className='mb-1'>
            <Col xs='8'>
              <Typography variant='body2'>
                {hit.name} <span style={{ color: '#6c757d' }}>{hit.businessId}</span>
              </Typography>
            </Col>
            <Col xs='4'>
              <Button size='small' onClick={() => this.applyPrhHit(hit)}>
                Käytä
              </Button>
            </Col>
          </Row>
        ))}
      </div>
    )
  }

  render() {
    const { partner, organisations, saving } = this.state
    const selectedOrganisation = organisations.find((o) => o.id === partner.organisationid) || null

    return (
      <div style={{ padding: 20, backgroundColor: 'white' }}>
        <Row>
          <Col>
            <Typography variant='h6'>{partner.id ? 'Muokkaa kumppania' : 'Lisää kumppani'}</Typography>
          </Col>
          <Col xs='auto'>
            <Button onClick={this.props.onCancel} disabled={saving}>
              Palaa listaan
            </Button>
          </Col>
        </Row>

        <Row className='mt-4'>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              required
              size='small'
              label='Nimi'
              value={partner.name}
              onChange={(e) => this.handleTextChange(e, 'name')}
              error={!this.isValid()}
              helperText={!this.isValid() ? 'Nimi on pakollinen' : 'Näkyy kampanjalistalla ja raportilla'}
            />
          </Col>
          <Col xs='12' md='4'>
            <Autocomplete
              size='small'
              options={organisations}
              value={selectedOrganisation}
              getOptionLabel={(option) => option.name || option.id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => this.handleOrganisationChange(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Organisaatio'
                  helperText='Valinnainen — liittää kumppanin olemassa olevaan asiakasyritykseen'
                />
              )}
            />
          </Col>
          <Col xs='12' md='4' className='mt-2'>
            <FormControlLabel
              control={<Switch size='small' checked={partner.state === 'active'} onChange={this.handleStateSwitch} />}
              label={<span style={{ fontSize: 13 }}>{partner.state === 'active' ? 'Aktiivinen' : 'Tauolla'}</span>}
            />
          </Col>
        </Row>

        <Row className='mt-4'>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Yhteyshenkilö'
              value={partner.contactname}
              onChange={(e) => this.handleTextChange(e, 'contactname')}
            />
          </Col>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Sähköposti'
              value={partner.email}
              onChange={(e) => this.handleTextChange(e, 'email')}
            />
          </Col>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Puhelin'
              value={partner.phone}
              onChange={(e) => this.handleTextChange(e, 'phone')}
            />
          </Col>
        </Row>

        <Divider sx={{ mt: 4, mb: 1 }} />
        <Typography variant='subtitle2' sx={{ mt: 2 }}>
          Laskutus
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Palkkiot maksetaan tämän kumppanin tiedoilla. Kampanjat perivät nämä sellaisenaan.
        </Typography>

        <Row className='mt-3'>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              size='small'
              label='Y-tunnus'
              value={partner.businessid}
              onChange={(e) => this.handleTextChange(e, 'businessid')}
            />
          </Col>
          <Col xs='12' md='2'>
            <TextField
              fullWidth
              select
              size='small'
              label='Palkkion alv%'
              value={partner.vatpercent}
              onChange={(e) => this.handleNumberChange(e, 'vatpercent')}
            >
              {VAT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option} %
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='12' md='3'>
            <TextField
              fullWidth
              select
              size='small'
              label='Laskutustapa'
              value={partner.invoicingmode}
              onChange={(e) => this.handleTextChange(e, 'invoicingmode')}
            >
              {INVOICING_MODES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='12' md='3'>
            <TextField
              fullWidth
              size='small'
              label='IBAN'
              value={partner.iban}
              onChange={(e) => this.handleTextChange(e, 'iban')}
              InputProps={{ startAdornment: <InputAdornment position='start'>FI</InputAdornment> }}
            />
          </Col>
        </Row>

        <Row className='mt-3'>
          <Col xs='12' md='6'>
            <Button size='small' onClick={this.searchPrh} disabled={this.state.prhSearching}>
              Hae yritysrekisteristä
            </Button>
            {this.renderPrhResults()}
          </Col>
        </Row>

        <Row className='mt-4'>
          <Col xs='12' md='8'>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size='small'
              label='Muistiinpanot'
              value={partner.notes}
              onChange={(e) => this.handleTextChange(e, 'notes')}
            />
          </Col>
        </Row>

        <Row className='mt-5'>
          <Col>
            <Button onClick={this.props.onCancel} disabled={saving}>
              Peruuta
            </Button>{' '}
            <Button variant='contained' color='primary' onClick={this.handleSave} disabled={saving || !this.isValid()}>
              {saving ? 'Tallennetaan...' : 'Tallenna'}
            </Button>
          </Col>
        </Row>
      </div>
    )
  }
}

export default withFirebase(AffiliatePartnerEditor)
